import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Canonical role helpers — accept legacy "purchase_manager" too
const normRole = (r) => {
  const s = String(r || "").toLowerCase().trim();
  if (s === "purchase_manager" || s === "purchase-manager") return "purchase-manager";
  return s;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = authed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(false);
  };

  const roleFlags = useMemo(() => {
    const role = normRole(user?.role);
    return {
      role,
      isAdmin: role === "admin",
      isPurchaseManager: role === "purchase-manager",
      isViewer: role === "viewer",
      canWrite: role === "admin" || role === "purchase-manager", // viewers = read-only
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, ...roleFlags }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
